import React from 'react'

import { Spinner } from 'components/Spinner/Spinner'

export interface ButtonProps extends React.HTMLAttributes<HTMLButtonElement> {
  /**
   * Switch between the different variants.
   * @default "primary"
   */
  variant?: 'primary' | 'secondary' | 'tertiary'
  /**
   * Show a text content instead of send a children.
   */
  label?: string
  /**
   * If `true`, the button will show a spinner.
   */
  isLoading?: boolean
  /**
   * If `true`, the button will be disabled.
   * @default "false"
   */
  isDisabled?: boolean
  /**
   * The label to show in the button when `isLoading` is true
   * If no text is passed, it only shows the spinner
   * @default "Loading..."
   */
  loadingText?: string
  /**
   * If `true`, the button will take up the full width of its container.
   */
  isFullWidth?: boolean
  /**
   * The html button type to use.
   * @default "button"
   */
  type?: 'button' | 'reset' | 'submit'
  /**
   * If added, the button will show an icon before the button's label.
   * @type React.ReactElement
   */
  leftIcon?: React.ReactElement
  /**
   * If added, the button will show an icon after the button's label.
   * @type React.ReactElement
   */
  rightIcon?: React.ReactElement
  /**
   * Replace the spinner component when `isLoading` is set to `true`
   * @type React.ReactElement
   */
  spinner?: React.ReactElement
  /**
   * It determines the placement of the spinner when isLoading is true
   * @default "start"
   */
  spinnerPlacement?: 'start' | 'end'
  /**
   * Show the button with smaller padding
   * Overrides "size" prop if enabled
   * @default "false"
   */
  compact?: boolean
  /**
   * Sets the button padding and font size
   * @default "normal"
   */
  size?: 'tiny' | 'small' | 'normal' | 'large' | 'big'
}

export const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  (props, ref) => {
    const {
      isLoading,
      children,
      type = 'button',
      spinnerPlacement = 'start',
      loadingText = 'Loading...',
      label,
      leftIcon,
      rightIcon,
      isDisabled = false,
      isFullWidth,
      variant = 'primary',
      compact = false,
      size = 'normal',
      ...rest
    } = props

    const variants = {
      primary:
        'text-white bg-flamingo border border-transparent hover:bg-flamingo-600 active:bg-full disabled:bg-gray-300',
      secondary:
        'text-flamingo bg-white border border-flamingo hover:text-flamingo-600 hover:border-flamingo-600 active:border-full active:text-full disabled:border-gray-300 disabled:text-gray-300',
      tertiary:
        'text-flamingo bg-white hover:text-flamingo-600 active:text-full',
    }

    const sizes = {
      tiny: 'text-xs px-2 py-1',
      small: 'text-sm px-4 py-2',
      normal: 'text-base px-8 py-4',
      large: 'text-lg px-10 py-6',
      big: 'text-xl px-12 py-8',
    }

    const extraStyles = [
      isFullWidth ? 'w-full' : 'w-auto',
      compact ? 'px-4 py-2' : sizes[size],
    ]

    return (
      <button
        ref={ref}
        type={type}
        disabled={isDisabled}
        className={`${variants[variant]} ${extraStyles.join(
          ' '
        )} text-sm rounded-lg  disabled:cursor-default flex justify-center items-center space-x-4`}
        {...rest}
      >
        {isLoading && spinnerPlacement === 'start' && <Spinner />}
        {!!leftIcon && leftIcon}
        <span>{isLoading ? loadingText : children || label}</span>
        {!!rightIcon && rightIcon}
        {isLoading && spinnerPlacement === 'end' && <Spinner />}
      </button>
    )
  }
)
